/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import { ChangeEvent, FC, useCallback, useMemo, useRef, useState } from 'react';
import Text from '../../Text/Text';
import InputMovablePlaceholder from './components/InputMovablePlaceholder/InputMovablePlaceholder';
import InputResetIcon from './components/InputResetIcon/InputResetIcon';
import SpinnerInInput from './components/SpinnerInInput/SpinnerInInput';
import { onChangeInputValueHandler } from './helpers';
import './style.scss';
import { IInputProps, InputType, TInputType } from './types';

const numberTypes: TInputType[] = [InputType.NUMBER, InputType.FLOAT_NUMBER];

const Input: FC<IInputProps> = ({
  value,
  type = InputType.TEXT,
  onChangeValue,
  //
  prevComponent,
  nextComponent,
  //
  disabled,
  validation,
  resetIcon,
  loading,
  placeholder,
  movablePlaceholder,
  required,
  disableOnFocusOnInputWrapper,
  disableRemoveUnnecessaryZeros,
  className,
  contentClassName,
  inputClassName,
  ...props
}: IInputProps): JSX.Element => {
  const [focused, setFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isNumberType = useMemo((): boolean => numberTypes.includes(type), [numberTypes, type]);
  const currentValue = value ?? '';
  const isValueAvailable: boolean = Boolean(currentValue);
  const showResetIcon = Boolean(resetIcon && isValueAvailable && !loading);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeInputValueHandler({ event, disableRemoveUnnecessaryZeros, type, isNumberType, maxLength: props.maxLength, onChangeValue });
  };

  const onReset = useCallback(() => {
    onChangeValue(undefined);
    inputRef.current?.focus();
  }, [inputRef, onChangeValue]);

  const onWrapperFocus = () => {
    if (disableOnFocusOnInputWrapper || disabled || loading) return;
    inputRef.current?.focus();
    onFocus();
  };

  const onFocus = () => {
    if (typeof props.onFocus === 'function') props.onFocus();
    if (movablePlaceholder) setFocused(true);
  };

  const onBlur = () => {
    if (typeof props.onBlur === 'function') props.onBlur();
    if (movablePlaceholder) setFocused(false);
  };

  const _contentClassName = useMemo((): string => {
    return classNames('input-new__content', contentClassName, {
      'input-error': validation && !validation.isValid,
      'input-new__content_disabled': disabled,
      'input-new__content_loading': !disabled && loading,
    });
  }, [contentClassName, validation, disabled, loading]);

  const _inputClassName = useMemo((): string => {
    return classNames('input-new', inputClassName, {
      'input-new_with-movable-placeholder': placeholder && movablePlaceholder,
      'input-new_pr': !showResetIcon && !nextComponent,
    });
  }, [inputClassName, placeholder, movablePlaceholder, showResetIcon, nextComponent]);

  return (
    <div className={classNames('d-flex flex-column', className)}>
      <div onClick={onWrapperFocus} className={_contentClassName}>
        {prevComponent || null}

        <input
          {...props}
          ref={inputRef}
          type={isNumberType ? InputType.TEXT : type}
          value={currentValue}
          placeholder={movablePlaceholder ? undefined : placeholder}
          onInput={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          className={_inputClassName}
        />

        <SpinnerInInput loading={loading} />
        <InputResetIcon visible={showResetIcon} onReset={onReset} />

        {nextComponent || null}

        <InputMovablePlaceholder
          placeholder={placeholder}
          movablePlaceholder={movablePlaceholder}
          focused={isValueAvailable || focused}
          required={required}
        />
      </div>
      {validation?.errorText ? <Text type="error" text={validation.errorText} /> : null}
    </div>
  );
};

export default Input;
